// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import { Box, Button, CircularProgress, TextField, Tooltip } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNotify, usePermissions, useRecordContext, useRefresh, useTranslate } from 'react-admin'

import { canEditClientProfile, updateClientProfile } from './clientProfile'

/**
 * The "Client Name" control on the pet Edit form.
 *
 * Reported 2026-08-06: a clinic "cannot change the client name when they click
 * on edit at the top portion of vetpo". It was `<TextInput source="owner.name"
 * disabled />` — hardcoded off for every role.
 *
 * This deliberately does NOT use react-admin's `<TextInput>`. Registering the
 * field with the form would put `owner: { name: … }` into the pets PATCH, where
 * Payload treats `owner` as a relationship, reconciles the id and drops the
 * name — and PetEdit's AutoSave would then report "Saved" over an edit that
 * never happened. Instead this owns its own state and writes through the
 * scoped `PATCH /api/users/client-profile` endpoint, with an explicit Save so
 * the clinic sees a real success/failure per edit rather than a debounce.
 */
const ClientNameEditorInner = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const refresh = useRefresh()
  const record = useRecordContext()
  const { permissions } = usePermissions()

  const owner = record?.owner
  // `owner` is a populated user object only when the caller may READ that user.
  // Before the Users read-scope fix a vet got the bare id here, which is
  // precisely why the clinic saw no client details at all.
  const ownerObj =
    typeof owner === 'object' && owner !== null ? (owner as Record<string, any>) : null
  // Deliberately NOT falling back to a bare relationship id. Post-fix, a bare
  // id means this caller genuinely may not read that client (e.g. a
  // specific-pets vet-share, which grants the pet without the owner). Offering
  // an edit there would present an empty box as if it were the client's name
  // and overwrite a name the clinic was never shown.
  const ownerId = ownerObj?.id ?? null
  const serverName: string = typeof ownerObj?.name === 'string' ? ownerObj.name : ''

  const [value, setValue] = useState(serverName)
  const [saving, setSaving] = useState(false)

  // Re-seed when the record arrives/refreshes, but never stomp an in-flight
  // edit: only follow the server when the user has not diverged from it.
  useEffect(() => {
    setValue((current) => (current === '' || current === serverName ? serverName : current))
  }, [serverName])

  const canEdit = canEditClientProfile(permissions?.role) && !!ownerId
  const trimmed = value.trim()
  const dirty = trimmed !== serverName
  // Gated on `dirty` (P71-4): an untouched field whose server name happens to
  // be empty must not show a red "too short" error on first render — only a
  // user's OWN edit down to <2 chars should block Save.
  const tooShort = dirty && trimmed.length < 2

  const handleSave = async () => {
    if (!ownerId || !dirty || tooShort) return
    setSaving(true)
    const result = await updateClientProfile({ ownerId, name: trimmed })
    setSaving(false)

    if (result.ok) {
      notify('pos.client_profile.saved', { type: 'success' })
      refresh()
      return
    }
    if (result.status === 403 || result.status === 404) {
      notify('pos.client_profile.not_allowed', { type: 'warning' })
      return
    }
    notify('pos.client_profile.save_error', { type: 'error' })
  }

  const field = (
    <TextField
      label={translate('pos.pet_edit.owner_name')}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      disabled={!canEdit || saving}
      error={tooShort}
      helperText={tooShort ? translate('pos.client_profile.name_too_short') : undefined}
      size="small"
      variant="filled"
      inputProps={{ 'data-testid': 'client-name-input', name: 'clientName', maxLength: 200 }}
      fullWidth
      sx={{ minWidth: 0 }}
    />
  )

  return (
    // Fills its form-grid cell (PetEdit); the read-only tooltip wrapper
    // must stretch too or the field collapses to its label width.
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        width: '100%',
        '& > span': { flex: 1, display: 'flex' },
      }}
    >
      {canEdit ? (
        field
      ) : (
        <Tooltip title={translate('pos.client_profile.read_only_hint')}>
          <span>{field}</span>
        </Tooltip>
      )}
      {canEdit && (
        <Button
          onClick={handleSave}
          disabled={!dirty || tooShort || saving}
          variant="outlined"
          size="small"
          data-testid="client-name-save"
          startIcon={saving ? <CircularProgress size={14} /> : undefined}
          sx={{ mt: 0.5 }}
        >
          {translate('pos.client_profile.save')}
        </Button>
      )}
    </Box>
  )
}

/**
 * Keyed by PET (mirrors AlertRulesSection's wrapper — vic/vetpo#36 pattern).
 * PetEdit keeps this subtree mounted across a /pets/A -> /pets/B route
 * change, so an unkeyed instance carried a dirty, unsaved edit of A's owner
 * name from one pet to the next and could save it onto B's owner instead.
 * With the key, every pet switch unmounts the old editor and mounts a fresh
 * one seeded from that pet's own record.
 */
const ClientNameEditor = () => {
  const record = useRecordContext()
  return <ClientNameEditorInner key={record?.id != null ? String(record.id) : 'no-pet'} />
}

export default ClientNameEditor
